/**
 * Fetch every row of a list that may be longer than 1,000 rows.
 *
 * Supabase's API returns at most 1,000 rows per request (the project's
 * "Max rows" setting, default 1,000). A plain `.select()` on a longer list is
 * silently cut at 1,000 with no error. The load test on 27 Sep 2026 found this
 * cutting Year 5 coverage figures and hiding 447 SUR-523 questions from mock exams.
 *
 * Usage — pass a function that builds the query for one page. The query must have
 * a stable order (add `.order('id')` as a tie-breaker) so pages don't overlap:
 *
 *   const rows = await fetchAllRows((from, to) =>
 *     supabase.from('mcqs').select('id').eq('module_id', id).order('id').range(from, to));
 *
 * Assumes the project's Max rows setting is at least PAGE_SIZE (1,000).
 */
export const PAGE_SIZE = 1000;

export async function fetchAllRows<T>(
  buildPage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
  maxRows = 20_000,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; from < maxRows; from += PAGE_SIZE) {
    const to = Math.min(from + PAGE_SIZE, maxRows) - 1;
    const { data, error } = await buildPage(from, to);
    if (error) throw error;
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < to - from + 1) break;
  }
  return all;
}
